import { DecryptedPatient, DecryptedService, Gender } from '@icure/cardinal-sdk'
import { Form } from '@icure/form/components/model'
import { VersionedData } from '@icure/form/generic'
import { normalizeCode } from '@icure/form/utils/code-utils'
import { InitialEvaluationData, MedicalOverview, PatientData, WellBeingSummary } from '../components/Patients/components/MedicalReportPDF/types'
import { convertTimeInSeconds, getAge, getAgeDescription } from './date'
import { getAllFields, getFields, getOptions } from './fields'
import { getAnyNumberContent, getAnyTextContent, getCodeNumberValue, getServiceForLabel, getServicesForLabel, svcMeasure, svcText } from './serviceDataExtraction'
import { populateFormTranslations, translate } from './translate'

export interface ExtractReportDataParams {
  indexedServices: VersionedData<DecryptedService>
  patient: DecryptedPatient
  language: string
  form: Form
}

export interface ReportData {
  patientData: PatientData
  medicalOverview: MedicalOverview
  initialEvaluationData: InitialEvaluationData
  wellBeingSummary: WellBeingSummary
}

const scale = (value: number | undefined, thresholds: number[]): number | undefined => {
  if (value === undefined) return undefined
  const idx = thresholds.findIndex((t, i) => i > 0 && value < t)
  return idx === -1 ? thresholds.length - 1 : idx - 1
}

const extractIaScore = (service: DecryptedService | undefined, codePrefix: string): number | undefined => {
  if (!service?.codes) return undefined
  const scoreCode = service.codes.find((c) => c.id?.startsWith(codePrefix))
  if (!scoreCode?.id) return undefined
  const score = parseFloat(scoreCode.id.split('|')[1])
  return isNaN(score) ? undefined : Math.min(100, Math.max(0, score * 10))
}

export async function extractReportData({ indexedServices, patient, language, form }: ExtractReportDataParams): Promise<ReportData> {
  await populateFormTranslations(form)

  const translationTable = form.translations?.find((t) => t.language === language)?.translations ?? {}

  const codeToText: Record<string, string> = {}
  for (const field of getAllFields(form)) {
    if (field.options) {
      for (const [code, i18nKey] of Object.entries(field.options)) {
        const translated = translationTable[i18nKey as string]
        if (translated) {
          codeToText[code] = translated
        }
      }
    }
  }

  const resolveCode = (code: string): string => codeToText[code] ?? code
  const resolveCodes = (text: string): string => text.replace(/\b([A-Z]{2,}\d+\|\d+\|\d+)\b/g, (_match, code) => resolveCode(code))
  const labelToText = (label: string): string | undefined => {
    const res = getFields(form, label)
      .map((f) => translationTable[f.shortLabel ?? ''] ?? f.shortLabel)
      .join(', ')
    return res.length ? res : undefined
  }
  const optionToText = (label: string): string | undefined => {
    if (codeToText[label]) return codeToText[label]
    const res = getOptions(form, label)
      .map((f) => translationTable[f[1]] ?? f[1])
      .join(', ')
    return res.length ? res : undefined
  }

  const patientData: PatientData = {
    name: `${patient.firstName ?? ''} ${patient.lastName ?? ''}`,
    dateOfBirth: patient.dateOfBirth ? patient.dateOfBirth.toString().replace(/(....)(..)(..)/, '$3.$2.$1') : '',
    age: `${getAgeDescription(patient.dateOfBirth)}`,
    birthSex: patient.birthSex === Gender.Male ? 'Homme' : patient.birthSex === Gender.Female ? 'Femme' : 'Autre',
    bloodGroup: svcText(language, getServiceForLabel(indexedServices, 'GI08')),
    waistCircumference: svcMeasure(language, getServiceForLabel(indexedServices, 'GI04')),
    weight: svcMeasure(language, getServiceForLabel(indexedServices, 'GI01')),
    height: svcMeasure(language, getServiceForLabel(indexedServices, 'GI02')),
    bloodPressure: `${svcMeasure(language, getServiceForLabel(indexedServices, 'GI05'))}/ ${svcMeasure(language, getServiceForLabel(indexedServices, 'GI06'))}`,
    heartRate: svcMeasure(language, getServiceForLabel(indexedServices, 'GI07')),
    bmi: svcMeasure(language, getServiceForLabel(indexedServices, 'GI03')),
  }

  const medicalOverview: MedicalOverview = {
    allergies: Object.values(getServicesForLabel(indexedServices, 'RM2')).map((vs) => resolveCodes(svcText(language, vs[0]?.value, labelToText, optionToText))),
    chronicTreatment: Object.values(getServicesForLabel(indexedServices, 'RM1')).map((vs) => resolveCodes(svcText(language, vs[0]?.value, labelToText, optionToText))),
    medicalHistory: Object.values(getServicesForLabel(indexedServices, 'RM3')).map((vs) => resolveCodes(svcText(language, vs[0]?.value, labelToText, optionToText))),
    surgicalHistory: Object.values(getServicesForLabel(indexedServices, 'RM4')).map((vs) => resolveCodes(svcText(language, vs[0]?.value, labelToText, optionToText))),
    familyHistory: Object.values(getServicesForLabel(indexedServices, 'RM5')).map((vs) => resolveCodes(svcText(language, vs[0]?.value, labelToText, optionToText))),
    lifestyle: [],
  }

  const wellBeingSummary: WellBeingSummary = [
    { heading: await translate(language, 'report', 'État général de santé'), summary: [{ description: svcText(language, getServiceForLabel(indexedServices, 'RE01')) }] },
    {
      heading: await translate(language, 'report', 'Contexte social et environnemental'),
      summary: [{ description: svcText(language, getServiceForLabel(indexedServices, 'RE02')) }],
    },
    {
      heading: await translate(language, 'report', 'Santé mentale et qualité du sommeil'),
      summary: [{ description: [svcText(language, getServiceForLabel(indexedServices, 'RE03')), svcText(language, getServiceForLabel(indexedServices, 'RE03B'))].join('\n\n') }],
    },
    {
      heading: await translate(language, 'report', 'Activité physique et Alimentation'),
      summary: [{ description: [svcText(language, getServiceForLabel(indexedServices, 'RE04')), svcText(language, getServiceForLabel(indexedServices, 'RE04B'))].join('\n\n') }],
    },
    {
      heading: await translate(language, 'report', "Consommation d'alcool et addictions"),
      summary: [{ description: svcText(language, getServiceForLabel(indexedServices, 'RE05')) }],
    },
    { heading: await translate(language, 'report', 'Qualité et espérance de vie'), summary: [{ description: svcText(language, getServiceForLabel(indexedServices, 'RE06')) }] },
    { heading: await translate(language, 'report', 'Parcours santé'), summary: [{ description: svcText(language, getServiceForLabel(indexedServices, 'RE07')) }] },
    {
      heading: await translate(language, 'report', 'Résultats de la prise de sang'),
      summary: [{ description: svcText(language, getServiceForLabel(indexedServices, 'RE08')) }],
    },
    {
      heading: await translate(language, 'report', 'Conclusion. Recommandations principales'),
      summary: [{ description: svcText(language, getServiceForLabel(indexedServices, 'RE09')) }],
    },
  ]

  const sportScore =
    (getServiceForLabel(indexedServices, 'PA05')
      ?.codes?.map((c) => normalizeCode(c))
      .reduce((sum, code) => sum + (parseInt(code.code ?? '1') - 1) * 3600, 0) ?? 0) +
    (getAnyNumberContent(getServiceForLabel(indexedServices, 'PA09'), language) ?? 5) *
      (getServiceForLabel(indexedServices, 'PA10')
        ?.codes?.map((c) => normalizeCode(c))
        .reduce((sum, code) => sum + (parseInt(code.code ?? '1') - 1) * 30 * 60, 0) ?? 0) +
    (getAnyNumberContent(getServiceForLabel(indexedServices, 'PA12'), language) ?? 5) *
      (getServiceForLabel(indexedServices, 'PA13')
        ?.codes?.map((c) => normalizeCode(c))
        .reduce((sum, code) => sum + (parseInt(code.code ?? '1') - 1) * 10 * 60, 0) ?? 0) +
    convertTimeInSeconds(getAnyTextContent(getServiceForLabel(indexedServices, 'PA19'), language))

  const sportProgress = Math.min(100, Math.max(0, Math.round((sportScore / 3600 / 5) * 100)))

  const mentalHealthScore12 = getAnyNumberContent(getServiceForLabel(indexedServices, 'SM12'), language) ?? null
  const mentalHealthScore56 = getAnyNumberContent(getServiceForLabel(indexedServices, 'SM56'), language) ?? null

  const mentalHealthProgress =
    mentalHealthScore12 !== null && mentalHealthScore56 !== null
      ? ((mentalHealthScore12 * 100) / 40 + (mentalHealthScore56 * 100) / 17) / 2
      : mentalHealthScore12 !== null
        ? (mentalHealthScore12 * 100) / 40
        : mentalHealthScore56 !== null
          ? (mentalHealthScore56 * 100) / 17
          : undefined

  const s12 = mentalHealthScore12 ?? 0
  const s56 = mentalHealthScore56 ?? 0

  const nutritionScore =
    ['NH01', 'NH02', 'NH06'].map((label) => getCodeNumberValue(getServiceForLabel(indexedServices, label))).reduce((sum: number, v) => sum + (v ? 5 - v : 5), 0) +
    ['NH03', 'NH04', 'NH07', 'NH08', 'NH09'].map((label) => getCodeNumberValue(getServiceForLabel(indexedServices, label))).reduce((sum: number, v) => sum + (v ? v : 0), 0) +
    ((getCodeNumberValue(getServiceForLabel(indexedServices, 'NH05')) ?? 0) < 2 ? 0 : 5)

  const nutritionScoreProgress = Math.min(100, Math.max(0, Math.round((nutritionScore / 40) * 100)))

  const sleepQualityScore = getAnyNumberContent(getServiceForLabel(indexedServices, 'CD18'), language) ?? 0
  const sleepQualityProgress = Math.min(100, Math.max(0, Math.round(((64 - sleepQualityScore) / 64) * 100)))

  const iaSportProgress = extractIaScore(getServiceForLabel(indexedServices, 'RE04'), 'PREVENTI-PA-SCORE')
  const iaMentalHealthProgress = extractIaScore(getServiceForLabel(indexedServices, 'RE03'), 'PREVENTI-MH-SCORE')
  const iaSleepProgress = extractIaScore(getServiceForLabel(indexedServices, 'RE03B'), 'PREVENTI-SLEEP-SCORE')
  const iaNutritionProgress = extractIaScore(getServiceForLabel(indexedServices, 'RE04B'), 'PREVENTI-NH-SCORE')
  const iaAddictionsProgress = extractIaScore(getServiceForLabel(indexedServices, 'RE05'), 'PREVENTI-DEP-SCORE')

  const mentalHealthIdx = mentalHealthProgress ? (s12 < 14 && s56 < 10 ? 0 : s12 < 27 && s56 < 14 ? 1 : 2) : undefined
  const nutritionScoreIdx = scale((nutritionScore + (iaNutritionProgress ?? nutritionScore)) / 2, [0, 20, 60, 100])
  const sleepQualityIndex = sleepQualityProgress < 30 ? '0' : '1'

  const colonCancersScreeningMissing =
    (getAge(patient.dateOfBirth) ?? 0) > 45 &&
    patient.birthSex === Gender.Male &&
    getServiceForLabel(indexedServices, 'SC32')
      ?.codes?.map((c) => normalizeCode(c))
      .some((c) => c.code === '3' || c.code === '4')
  const breastCancersScreeningMissing =
    (getAge(patient.dateOfBirth) ?? 0) > 45 &&
    patient.birthSex === Gender.Female &&
    getServiceForLabel(indexedServices, 'SC37')
      ?.codes?.map((c) => normalizeCode(c))
      .some((c) => c.code === '3' || c.code === '4')
  const uterineCancersScreeningMissing =
    (getAge(patient.dateOfBirth) ?? 0) > 45 &&
    patient.birthSex === Gender.Female &&
    getServiceForLabel(indexedServices, 'SC38')
      ?.codes?.map((c) => normalizeCode(c))
      .some((c) => c.code === '4' || c.code === '5')

  const initialEvaluationData: InitialEvaluationData = {
    indicators: [
      {
        title: await translate(language, 'report', 'Activité physique'),
        progress: sportProgress,
        iaProgress: iaSportProgress,
        imgSrc: '/report/initialEvaluation/runner.png',
        description: [await translate(language, 'report', 'SportIndex|' + scale((sportProgress + (iaSportProgress ?? sportProgress)) / 2, [0, 10, 30, 70, 80, 90, 100]))],
      },
      {
        title: await translate(language, 'report', 'Santé mentale'),
        progress: mentalHealthProgress ? 100 - mentalHealthProgress : undefined,
        iaProgress: iaMentalHealthProgress,
        imgSrc: '/report/initialEvaluation/brain.png',
        description: mentalHealthIdx !== undefined ? [await translate(language, 'report', 'MentalHealthIndex|' + (2 - mentalHealthIdx))] : [],
      },
      {
        title: await translate(language, 'report', 'Nutrition'),
        progress: nutritionScoreProgress,
        iaProgress: iaNutritionProgress,
        imgSrc: '/report/initialEvaluation/apple.png',
        description: [await translate(language, 'report', 'NutritionScoreIndex|' + nutritionScoreIdx)],
      },
      {
        title: await translate(language, 'report', 'Qualité du sommeil'),
        progress: sleepQualityProgress,
        iaProgress: iaSleepProgress,
        imgSrc: '/report/initialEvaluation/sleep.png',
        description: [await translate(language, 'report', 'SleepQualityIndex|' + sleepQualityIndex)],
      },
      {
        title: await translate(language, 'report', 'Addictions'),
        progress: iaAddictionsProgress,
        iaProgress: iaAddictionsProgress,
        imgSrc: '/report/initialEvaluation/cannabis.png',
        description: [
          iaAddictionsProgress ? await translate(language, 'report', 'AddictionScoreIndex|' + (iaAddictionsProgress < 50 ? 1 : 0)) : '',
          (iaAddictionsProgress ?? 100) < 80 ? await translate(language, 'report', 'AddictionTobaccoAlcohol') : undefined,
        ].filter((d) => d?.length) as string[],
      },
      {
        title: await translate(language, 'report', 'Parcours Santé'),
        progress: !colonCancersScreeningMissing && !breastCancersScreeningMissing && !uterineCancersScreeningMissing ? 100 : 50,
        imgSrc: '/report/initialEvaluation/person.png',
        description: [
          !colonCancersScreeningMissing && !breastCancersScreeningMissing && !uterineCancersScreeningMissing
            ? await translate(language, 'report', 'ScreeningDone' + patient.birthSex)
            : undefined,
          colonCancersScreeningMissing ? await translate(language, 'report', 'ColonCancerScreeningNeeded') : undefined,
          breastCancersScreeningMissing ? await translate(language, 'report', 'BreastCancerScreeningNeeded') : undefined,
          uterineCancersScreeningMissing ? await translate(language, 'report', 'UterineCancerScreeningNeeded') : undefined,
        ].filter((d) => d?.length) as string[],
      },
    ],
  }

  return { patientData, medicalOverview, initialEvaluationData, wellBeingSummary }
}
